import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { CreateInvoiceIcon } from "components/icons/CreateInvoiceIcon";
import {
  INVOICE_STATUSES,
  INVOICE_STATUSES_OPTIONS,
} from "constants/invoicing";
import {
  useGetEntityInvoiceQuery,
  useUpdateInvoiceMutation,
} from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { Link } from "react-router-dom";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Tag, TagTypeProps } from "components/DesignSystem/Tag/Tag";
import { FilledGreenWiteTick } from "components/icons/FilledGreenWhiteTick";
import { useModal } from "hooks/useModal";
import { InvoiceSlider } from "components/InvoiceList/InvoiceSlider";
import Slider from "components/DesignSystem/Slider/Slider";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { Divider } from "components/design/Divider";
import Loader from "components/DesignSystem/Loader/loader";

const INVOICE_TAG_TYPE: Record<string, TagTypeProps["tagType"]> = {
  [INVOICE_STATUSES.DRAFT]: "gray",
  [INVOICE_STATUSES.DUE]: "orange",
  [INVOICE_STATUSES.OVERDUE]: "red",
  [INVOICE_STATUSES.PAID]: "green",
  [INVOICE_STATUSES.PARTIALLY_PAID]: "orange",
  [INVOICE_STATUSES.UNCOLLECTIBLE]: "red",
  [INVOICE_STATUSES.VOID]: "gray",
};

const INVOICE_STATUS_LABEL: Record<string, string> = Object.fromEntries(
  INVOICE_STATUSES_OPTIONS.map(({ value, label }) => [value, label])
);

export const InvoiceDraftCard = ({
  groupId,
  size,
  invoiceId,
  entityId,
}: {
  groupId: string;
  size?: "regular" | "block";
  invoiceId: string;
  entityId: string;
}) => {
  const { alertToast } = useToast();
  const { link } = useConstructInternalLink();
  const sliderModal = useModal();

  const {
    data: invoice,
    isLoading: isInvoiceLoading,
    isFetching: isInvoiceFetching,
  } = useGetEntityInvoiceQuery(
    { groupId, entityId, invoiceId },
    { skip: !invoiceId || !entityId || !groupId }
  );

  const [updateInvoice, { isLoading }] = useUpdateInvoiceMutation();

  const invoiceStatus = invoice?.status;
  const isDraft = invoiceStatus === INVOICE_STATUSES.DRAFT;
  const isActive = Boolean(invoiceStatus) && !isDraft;
  const headerLabel = isDraft ? "Draft invoice" : "Invoice";

  const onMarkActive = async () => {
    try {
      await updateInvoice({
        invoiceId,
        entityId,
        groupId,
        payload: { status: INVOICE_STATUSES.DUE },
      }).unwrap();
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as Error
      );
    }
  };

  if (isInvoiceLoading || isInvoiceFetching) {
    return (
      <div className="t-min-w-72 sm:t-min-w-0">
        <ChatCard.Root size={size} className="t-w-full">
          <div className="t-flex t-items-center t-justify-center t-h-64">
            <Loader size="small" />
          </div>
        </ChatCard.Root>
      </div>
    );
  }

  return (
    <div className="t-min-w-72 sm:t-min-w-0">
      <ChatCard.Root size={size} className="t-w-full">
        <ChatCard.Header>
          <div className="t-flex t-items-center t-min-w-64 t-justify-between">
            <div className="t-items-center t-gap-1.5 t-flex">
              <CreateInvoiceIcon />
              {headerLabel}
            </div>
            <div>
              {isActive && invoiceStatus && (
                <Tag
                  tagType={INVOICE_TAG_TYPE[invoiceStatus]}
                  rounded
                  icon={false}
                >
                  {INVOICE_STATUS_LABEL[invoiceStatus]}
                </Tag>
              )}
            </div>
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div>
            {invoice?.customer?.company_name && (
              <div>
                <div className="t-text-subtext-sm t-text-text-30">
                  Customer name
                </div>
                <div className="t-font-medium t-text-body-sm-italic">
                  {invoice.customer.company_name}
                </div>
              </div>
            )}
            <div className="t-mt-3">
              <Divider type="dotted" />
            </div>
            {invoice?.invoice_date && (
              <div>
                <div className="t-text-subtext-sm t-text-text-30 t-mt-3">
                  Invoice date
                </div>
                <div className="t-font-medium t-text-body-sm-italic">
                  {dayjs(invoice.invoice_date).format(DISPLAY_DATE)}
                </div>
              </div>
            )}
            <div className="t-mt-3">
              <Divider type="dotted" />
            </div>
            <div className="t-font-semibold t-text-body-lg t-mt-5">
              <AmountSuperScript amount={Number(invoice?.total)} />
            </div>
            {isActive && (
              <div className="t-flex t-text-body-sm t-text-text-30 t-items-center t-mt-3">
                <FilledGreenWiteTick />
                <div className="t-ml-1">Marked as active</div>
              </div>
            )}
          </div>
          {isDraft ? (
            <Link to={link(`/books/invoices-and-customers/${invoiceId}`)}>
              <Button size="small" block>
                View and edit
              </Button>
            </Link>
          ) : (
            <Button size="small" onClick={sliderModal.open}>
              View
            </Button>
          )}
          {isDraft && (
            <Button
              size="small"
              customType="primary"
              onClick={onMarkActive}
              isLoading={isLoading}
            >
              Mark as active
            </Button>
          )}
        </ChatCard.Body>
      </ChatCard.Root>

      <Slider.Root open={sliderModal.isOpen} onOpenChange={sliderModal.close}>
        <Slider.Content open={sliderModal.isOpen}>
          <InvoiceSlider
            invoiceId={invoiceId}
            redirectLink={link(`/books/invoices-and-customers/${invoiceId}`)}
          />
        </Slider.Content>
      </Slider.Root>
    </div>
  );
};
